import { forwardRef, useRef, useState } from 'react';
import { Box, Checkbox, Collapse, Stack, Typography } from '@mui/material';
import formatDate, { formatUtcDate } from '../../../../utils/formatDate';
import TimesheetEntriesCollapse from './TimesheetEntriesCollapse';
import { TABLE_CELL_TYPOGRAPHY_STYLES } from '../styles';
import {
    SelectionStatus,
    useInvoicingSelectionContext,
} from '../../../Contexts/InvoicingSelectionContext';
import HierarchyConnectorHorizontal from '../HierarchyConnectorHorizontal';
import AnimatedExpandIcon from '../../../../Shared/Components/AnimatedExpandIcon';
import useHierarchyConnectorVertical from '../hooks/useHierarchyConnectorVertical';
import { currencyFormatter } from '../../../../utils/helpers';
import ExpenseEntriesCollapse from './ExpenseEntriesCollapse';

const InvoicingItemCollapse = (
    {
        hierarchyConnectorVerticalRef: parentHierarchyConnectorVerticalRef,
        bookingId,
        type,
        itemId,
        data,
    },
    ref
) => {
    const connectorParentRef = useRef(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const toggleExpand = () => {
        setIsExpanded((prevValue) => !prevValue);
    };

    const {
        mainContainerRef,
        expandIconRef,
        lastChildRef,
        hierarchyConnectorVerticalRef,
        renderConnector,
    } = useHierarchyConnectorVertical();

    const { getBookingItemSelectionState, handleSelectItem } = useInvoicingSelectionContext();
    const selectionStatus = getBookingItemSelectionState(bookingId, type, itemId);

    const hasSubItems =
        (type === 'timesheet' && data.lineItems?.length > 0) ||
        (type === 'expense' && data.items?.length > 0);

    return (
        <Box ref={mainContainerRef} position="relative">
            <Stack
                ref={ref}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                height={80}
                spacing={1}
                sx={{ cursor: hasSubItems ? 'pointer' : 'default' }}
                onClick={hasSubItems ? toggleExpand : undefined}
                aria-expanded={hasSubItems ? isExpanded : undefined}
                aria-controls={hasSubItems ? `subitem-collapse-${itemId}` : undefined}
            >
                <Box>
                    <Box
                        ref={connectorParentRef}
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            position: 'relative',
                            width: hasSubItems ? 74 : 43,
                            marginLeft: !hasSubItems ? '31px' : undefined,
                        }}
                    >
                        <HierarchyConnectorHorizontal
                            anchorRef={connectorParentRef}
                            verticalRef={parentHierarchyConnectorVerticalRef}
                        />
                        {hasSubItems && (
                            <AnimatedExpandIcon ref={expandIconRef} isExpanded={isExpanded} />
                        )}
                        <Checkbox
                            checked={selectionStatus === SelectionStatus.SELECTED}
                            indeterminate={selectionStatus === SelectionStatus.INDETERMINATE}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSelectItem(bookingId, type, itemId);
                            }}
                        />
                    </Box>
                </Box>
                {type === 'timesheet' && (
                    <>
                        <Typography
                            sx={{
                                ...TABLE_CELL_TYPOGRAPHY_STYLES,
                                width: '12.2%',
                                maxWidth: 208,
                            }}
                        >
                            Timesheets&nbsp;({data.lineItems?.length || 0})
                        </Typography>
                        <Typography
                            sx={{
                                ...TABLE_CELL_TYPOGRAPHY_STYLES,
                                fontSize: 12,
                                fontWeight: 400,
                                '& span': { fontSize: 14, fontWeight: 600 },
                                width: '13.6%',
                                maxWidth: 232,
                            }}
                        >
                            Week ending:&nbsp;
                            <span>{formatUtcDate(data.weekEnding)}</span>
                        </Typography>
                        <Typography
                            sx={{
                                ...TABLE_CELL_TYPOGRAPHY_STYLES,
                                fontSize: 12,
                                fontWeight: 400,
                                lineHeight: '16px',
                                '& span': { fontSize: 14 },
                                flexGrow: 1,
                                maxWidth: '70%',
                            }}
                        >
                            Totals to: Bill:&nbsp;
                            <span>${data.totals?.totalToBill.toFixed(2)}</span>
                            &nbsp;/ Pay:&nbsp;
                            <span>${data.totals?.totalToPay.toFixed(2)}</span>
                        </Typography>
                    </>
                )}
                {type === 'deposit' && (
                    <>
                        <Typography
                            sx={{
                                ...TABLE_CELL_TYPOGRAPHY_STYLES,
                                width: '12.2%',
                                maxWidth: 208,
                            }}
                        >
                            Deposit
                        </Typography>
                        <Typography
                            sx={{
                                ...TABLE_CELL_TYPOGRAPHY_STYLES,
                                '& span': { fontWeight: 700 },
                                width: '13.6%',
                                maxWidth: 232,
                            }}
                        >
                            <span>Amount:</span> ${Number(data.amount).toFixed(2)}
                        </Typography>
                        <Typography
                            sx={{
                                ...TABLE_CELL_TYPOGRAPHY_STYLES,
                                '& span': { fontWeight: 700 },
                                flexGrow: 1,
                            }}
                        >
                            <span>Posted date:</span> {formatDate(data.postedDate)}
                        </Typography>
                    </>
                )}
                {type === 'expense' && (
                    <>
                        <Typography
                            sx={{
                                ...TABLE_CELL_TYPOGRAPHY_STYLES,
                                width: '12.2%',
                                maxWidth: 208,
                            }}
                        >
                            Expenses
                        </Typography>
                        <Typography
                            sx={{
                                ...TABLE_CELL_TYPOGRAPHY_STYLES,
                                fontSize: 12,
                                fontWeight: 400,
                                '& span': { fontSize: 14, fontWeight: 600 },
                                width: '13.6%',
                                maxWidth: 232,
                            }}
                        >
                            <span>BKG-{bookingId}</span>
                        </Typography>
                        <Typography
                            sx={{
                                ...TABLE_CELL_TYPOGRAPHY_STYLES,
                                fontSize: 12,
                                fontWeight: 400,
                                lineHeight: '16px',
                                '& span': { fontSize: 14 },
                                flexGrow: 1,
                                maxWidth: '70%',
                            }}
                        >
                            Total to bill:&nbsp;
                            {currencyFormatter(data?.totals?.totalToBill)}
                        </Typography>
                    </>
                )}
            </Stack>

            {hasSubItems && (
                <Collapse in={isExpanded} id={`subitem-collapse-${itemId}`}>
                    {renderConnector(isExpanded)}
                    <Box sx={{ ml: 0, px: '26px' }}>
                        {type === 'timesheet' && (
                            <TimesheetEntriesCollapse
                                bookingId={bookingId}
                                timesheet={data}
                                hierarchyConnectorVerticalRef={hierarchyConnectorVerticalRef}
                                lastChildRef={lastChildRef}
                            />
                        )}
                        {type === 'expense' && (
                            <ExpenseEntriesCollapse
                                bookingId={bookingId}
                                expense={data}
                                hierarchyConnectorVerticalRef={hierarchyConnectorVerticalRef}
                                lastChildRef={lastChildRef}
                            />
                        )}
                    </Box>
                </Collapse>
            )}
        </Box>
    );
};

export default forwardRef(InvoicingItemCollapse);
